import React, { useState, useEffect } from "react";
import {
  UsersIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  PhoneIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import adminAPI from "../utils/api";
import toast from "react-hot-toast";
import ClientModal from "../components/ClientModal";
import ConfirmationModal from "../components/ConfirmationModal";

const Clients = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create', 'edit', 'view'
  const [selectedClient, setSelectedClient] = useState(null);
  const [initialTab, setInitialTab] = useState("info");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
    count: 0,
  });

  // Fetch clients from API with pagination and search
  const fetchClients = async (page = 1, search = "") => {
    try {
      setLoading(true);
      const filters = {
        role: "client",
        page: page,
      };

      if (search.trim()) {
        filters.search = search.trim();
      }

      const data = await adminAPI.getUsers(filters);

      // Handle paginated response
      if (data.results) {
        setClients(data.results);

        // Calculate total pages dynamically from API response
        let totalPages = 1;
        if (data.count > 0) {
          if (data.next || data.previous) {
            const currentPageSize = data.results.length;
            if (data.next) {
              totalPages = Math.ceil(data.count / currentPageSize);
            } else {
              totalPages = page;
            }
          } else {
            totalPages = 1;
          }
        }

        setPagination({
          currentPage: page,
          totalPages: totalPages,
          hasNext: !!data.next,
          hasPrevious: !!data.previous,
          count: data.count,
        });
      } else {
        // Fallback for non-paginated response
        setClients(Array.isArray(data) ? data : []);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          hasNext: false,
          hasPrevious: false,
          count: Array.isArray(data) ? data.length : 0,
        });
      }
    } catch (err) {
      console.error("Error fetching clients:", err);
      setError(err.message);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients(1, searchTerm);
  }, []);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchClients(1, searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchClients(newPage, searchTerm);
    }
  };

  // Handle delete client
  const handleDeleteClient = (client) => {
    setClientToDelete(client);
    setShowDeleteModal(true);
  };

  // Confirm delete client
  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;

    try {
      setDeleteLoading(true);
      await adminAPI.deleteUser(clientToDelete.id);
      toast.success("Client supprimé avec succès");
      fetchClients(); // Refresh the list
      setShowDeleteModal(false);
      setClientToDelete(null);
    } catch (err) {
      console.error("Error deleting client:", err);
      toast.error("Erreur lors de la suppression du client");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle view client
  const handleViewClient = (client) => {
    setSelectedClient(client);
    setModalMode("view");
    setInitialTab("info");
    setShowModal(true);
  };

  // Handle view client services
  const handleViewClientServices = (client) => {
    setSelectedClient(client);
    setModalMode("view");
    setInitialTab("services");
    setShowModal(true);
  };

  // Handle view client history
  const handleViewClientHistory = (client) => {
    setSelectedClient(client);
    setModalMode("view");
    setInitialTab("history");
    setShowModal(true);
  };

  // Handle edit client
  const handleEditClient = (client) => {
    setSelectedClient(client);
    setModalMode("edit");
    setInitialTab("info");
    setShowModal(true);
  };

  // Handle create client
  const handleCreateClient = () => {
    setSelectedClient(null);
    setModalMode("create");
    setInitialTab("info");
    setShowModal(true);
  };

  // No need for client-side filtering since we're doing server-side search
  const filteredClients = clients;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Erreur: {error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 btn-primary"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Clients</h2>
          <p className="text-gray-600">Gérez votre base de clients</p>
        </div>
        <button
          onClick={handleCreateClient}
          className="mt-4 sm:mt-0 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Nouveau Client</span>
        </button>
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 input-field"
          />
        </div>
      </div>

      {/* Clients Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Services Actifs
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Créé le
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <td
                  colSpan="6"
                  className="px-6 py-12 text-center text-gray-500"
                >
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
                  </div>
                </td>
              ) : filteredClients.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <UsersIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-lg font-medium">Aucun client trouvé</p>
                    <p className="text-sm">
                      Commencez par ajouter un nouveau client
                    </p>
                  </td>
                </tr>
              ) : (
                filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-600 font-medium text-sm">
                              {client.full_name?.charAt(0) || "C"}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {client.full_name || "N/A"}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {client.id?.slice(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-gray-900">
                          <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {client.email || "N/A"}
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {client.phone || "N/A"}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {client.activeServices?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {client.activeServices
                              .slice(0, 2)
                              .map((service, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full"
                                >
                                  {service}
                                </span>
                              ))}
                            {client.activeServices.length > 2 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                +{client.activeServices.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500 text-sm">
                            Aucun service
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          client.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {client.is_active ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {client.date_joined
                        ? new Date(client.date_joined).toLocaleDateString(
                            "fr-FR"
                          )
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => handleViewClient(client)}
                          className="text-gray-600 hover:text-gray-900 p-1 rounded"
                          title="Voir les détails"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleViewClientServices(client)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Voir les services"
                        >
                          <CreditCardIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleViewClientHistory(client)}
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Voir l'historique"
                        >
                          <CalendarDaysIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditClient(client)}
                          className="text-primary-600 hover:text-primary-900 p-1 rounded"
                          title="Modifier"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClient(client)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevious}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Précédent
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNext}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Affichage de{" "}
                <span className="font-medium">
                  {(pagination.currentPage - 1) * 10 + 1}
                </span>{" "}
                à{" "}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * 10, pagination.count)}
                </span>{" "}
                sur <span className="font-medium">{pagination.count}</span>{" "}
                résultats
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevious}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Précédent</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>

                {/* Page numbers */}
                {Array.from(
                  { length: pagination.totalPages },
                  (_, i) => i + 1
                ).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === pagination.currentPage
                        ? "z-10 bg-primary-50 border-primary-500 text-primary-600"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Suivant</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Client Modal */}
      <ClientModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        client={selectedClient}
        mode={modalMode}
        initialTab={initialTab}
        onSuccess={() => fetchClients(pagination.currentPage, searchTerm)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteClient}
        title="Supprimer le client"
        message={`Êtes-vous sûr de vouloir supprimer le client "${clientToDelete?.full_name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default Clients;
